import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  CircularProgress,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  LocalGasStation as FuelIcon,
  Build as RepairIcon,
  TireRepair as TireIcon,
  AttachMoney as MoneyIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  DirectionsCar as CarIcon,
  LocalShipping as TruckIcon,
} from "@mui/icons-material";
import {
  getExpenseReport,
  vehicleList,
  exportExpenseReportExcel,
} from "../../../Services/Services";

import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryBg,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};
const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};
const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};
const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const filterInputSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const selectFilterSx = {
  "& .MuiInputBase-root": { height: 34, ...sq, bgcolor: T.primaryBg },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .MuiSelect-select": { fontSize: "0.72rem" },
  "& .MuiInputLabel-root": { fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

// ── Expense Type Config ────────────────────────────────────────────
const EXPENSE_TYPE_CONFIG = {
  diesel: {
    label: "Diesel",
    color: "#d97706",
    bg: "#fffbeb",
    icon: <FuelIcon sx={{ fontSize: 14 }} />,
  },
  tyre: {
    label: "Tyre",
    color: "#0891b2",
    bg: "#ecfeff",
    icon: <TireIcon sx={{ fontSize: 14 }} />,
  },
  repair: {
    label: "Repair",
    color: T.red,
    bg: "#fef2f2",
    icon: <RepairIcon sx={{ fontSize: 14 }} />,
  },
  service: {
    label: "Service",
    color: "#7c3aed",
    bg: "#f5f3ff",
    icon: <RepairIcon sx={{ fontSize: 14 }} />,
  },
  rto: {
    label: "RTO",
    color: "#64748b",
    bg: "#f8fafc",
    icon: <ReceiptIcon sx={{ fontSize: 14 }} />,
  },
  insurance: {
    label: "Insurance",
    color: "#3b82f6",
    bg: "#eff6ff",
    icon: <ReceiptIcon sx={{ fontSize: 14 }} />,
  },
  tax: {
    label: "Tax",
    color: "#64748b",
    bg: "#f8fafc",
    icon: <MoneyIcon sx={{ fontSize: 14 }} />,
  },
  other: {
    label: "Other",
    color: "#64748b",
    bg: "#f8fafc",
    icon: <MoneyIcon sx={{ fontSize: 14 }} />,
  },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const formatCurrency = (a) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(a || 0);

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, color, sub }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1, sm: 1.5 },
      textAlign: "center",
      ...sq,
      border: `1.5px solid ${color}40`,
      borderTop: `3px solid ${color}`,
      bgcolor: `${color}08`,
      boxShadow: "none",
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: { xs: "0.55rem", sm: "0.65rem" },
        color: T.textSub,
        lineHeight: 1.2,
        mb: 0.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: { xs: "0.72rem", sm: "1rem" },
        fontWeight: 700,
        color,
        lineHeight: 1.2,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "block",
      }}
      title={value}
    >
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: "0.55rem", color: T.textMuted, mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ── MobileVehicleSummaryCard ───────────────────────────────────────
const MobileVehicleSummaryCard = ({ item, idx, expanded, onToggle }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 1,
      ...sq,
      border: `1.5px solid ${expanded ? T.primary : T.border}`,
      bgcolor: T.white,
      overflow: "hidden",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 60 }}>
      <Box
        sx={{
          width: 56,
          flexShrink: 0,
          bgcolor: expanded ? T.primary : T.primaryBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
          transition: "background-color 0.2s",
          gap: 0.25,
        }}
      >
        <TruckIcon
          sx={{ fontSize: 20, color: expanded ? T.white : T.primary }}
        />
        <Typography
          sx={{
            fontSize: "0.48rem",
            fontWeight: 700,
            color: expanded ? T.white : T.primary,
          }}
        >
          #{idx + 1}
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 1.2,
          py: 0.75,
          minWidth: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.82rem",
            color: T.text,
            lineHeight: 1.2,
            mb: 0.15,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.vehicle}
        </Typography>
        <Typography sx={{ fontSize: "0.58rem", color: T.textSub }}>
          {item.count} expense{item.count > 1 ? "s" : ""}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "flex-end",
          px: 1,
          py: 0.75,
          minWidth: 100,
        }}
      >
        <Box sx={{ textAlign: "right" }}>
          <Typography
            sx={{ fontSize: "0.48rem", color: T.textMuted, fontWeight: 600 }}
          >
            Total
          </Typography>
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.8rem", color: T.red }}
          >
            {formatCurrency(item.total)}
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={onToggle}
          startIcon={
            expanded ? (
              <VisibilityOff sx={{ fontSize: 12 }} />
            ) : (
              <Visibility sx={{ fontSize: 12 }} />
            )
          }
          sx={{
            ...ghostBtn,
            height: 22,
            fontSize: "0.55rem",
            px: 0.7,
            "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
          }}
        >
          {expanded ? "Hide" : "View"}
        </Button>
      </Box>
    </Box>

    {expanded && (
      <Box
        sx={{
          bgcolor: T.primaryBg,
          borderTop: `1.5px solid ${T.border}`,
          p: 1.25,
        }}
      >
        <Grid container spacing={0.5}>
          {[
            [
              "Diesel",
              item.diesel,
              "#d97706",
              <FuelIcon sx={{ fontSize: 14, color: "#d97706" }} />,
            ],
            [
              "Service",
              item.service,
              "#7c3aed",
              <RepairIcon sx={{ fontSize: 14, color: "#7c3aed" }} />,
            ],
            [
              "Other",
              item.other,
              T.success,
              <MoneyIcon sx={{ fontSize: 14, color: T.success }} />,
            ],
          ].map(([label, val, color, icon]) => (
            <Grid item xs={4} key={label}>
              <Box
                sx={{
                  p: 0.5,
                  bgcolor: T.white,
                  border: `1px solid ${T.border}`,
                  ...sq,
                  textAlign: "center",
                }}
              >
                {icon}
                <Typography
                  sx={{ fontSize: "0.5rem", color: T.textSub, mt: 0.2 }}
                >
                  {label}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color }}>
                  {formatCurrency(val)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    )}
  </Paper>
);

// ── MobileExpenseCard ──────────────────────────────────────────────
const MobileExpenseCard = ({ row, idx, expanded, onToggle }) => {
  const typeConfig =
    EXPENSE_TYPE_CONFIG[row.expense_type] || EXPENSE_TYPE_CONFIG.other;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        bgcolor: T.white,
        overflow: "hidden",
        transition: "border-color 0.2s, box-shadow 0.2s",
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        <Box
          sx={{
            width: 58,
            flexShrink: 0,
            bgcolor: expanded ? T.primary : `${typeConfig.color}14`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            gap: 0.25,
          }}
        >
          {React.cloneElement(typeConfig.icon, {
            sx: { fontSize: 20, color: expanded ? T.white : typeConfig.color },
          })}
          <Typography
            sx={{
              fontSize: "0.46rem",
              fontWeight: 700,
              color: expanded ? T.white : T.primary,
            }}
          >
            #{idx + 1}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.2,
            py: 0.75,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.vehicle_number || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.62rem",
              color: T.textSub,
              fontWeight: 500,
              mb: 0.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.description || "No description"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.6,
              flexWrap: "wrap",
            }}
          >
            <Chip
              icon={React.cloneElement(typeConfig.icon, {
                sx: { fontSize: 9 },
              })}
              label={typeConfig.label}
              size="small"
              sx={{
                ...sq,
                bgcolor: typeConfig.bg,
                color: typeConfig.color,
                fontSize: "0.48rem",
                height: 15,
                border: `1px solid ${typeConfig.color}30`,
                "& .MuiChip-icon": { color: typeConfig.color, ml: 0.4 },
              }}
            />
            <Typography sx={{ fontSize: "0.55rem", color: T.textMuted }}>
              {formatDate(row.expense_date)}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "flex-end",
            px: 1,
            py: 0.75,
            minWidth: 95,
          }}
        >
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: "0.48rem", color: T.textMuted, fontWeight: 600 }}
            >
              Amount
            </Typography>
            <Typography
              sx={{ fontWeight: 700, fontSize: "0.8rem", color: T.red }}
            >
              {formatCurrency(row.amount)}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 22,
              fontSize: "0.55rem",
              px: 0.7,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "View"}
          </Button>
        </Box>
      </Box>

      {expanded && (
        <Box
          sx={{
            bgcolor: T.primaryBg,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.25,
          }}
        >
          <Stack spacing={0.75}>
            <Box>
              <Grid container spacing={0.4}>
                {[
                  ["Vehicle", row.vehicle_number || "—"],
                  ["Date", formatDate(row.expense_date)],
                  ["Type", typeConfig.label],
                  ["KM", row.km_reading ? `${row.km_reading} km` : "—"],
                ].map(([l, v]) => (
                  <Grid item xs={6} key={l}>
                    <Typography
                      sx={{
                        fontSize: "0.58rem",
                        color: T.textMuted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                    >
                      {l}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: T.text,
                      }}
                    >
                      {v}
                    </Typography>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      fontSize: "0.58rem",
                      color: T.textMuted,
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.7rem", fontWeight: 500, color: T.text }}
                  >
                    {row.description || "—"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                p: 0.75,
                bgcolor: T.white,
                border: `1px solid ${T.border}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  color: T.primary,
                  mb: 0.4,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                💳 Payment Info
              </Typography>
              <Grid container spacing={0.4}>
                {[
                  ["Payment Mode", row.payment_mode || "—"],
                  ["Vendor", row.vendor_name || "—"],
                ].map(([l, v]) => (
                  <Grid item xs={6} key={l}>
                    <Typography
                      sx={{
                        fontSize: "0.58rem",
                        color: T.textMuted,
                        fontWeight: 600,
                        textTransform: "uppercase",
                      }}
                    >
                      {l}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: T.text,
                      }}
                    >
                      {v}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box
              sx={{
                p: 0.75,
                bgcolor: T.redBg,
                border: `1.5px solid ${T.redBorder}`,
                ...sq,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.58rem",
                  color: T.textMuted,
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Total Amount
              </Typography>
              <Typography
                sx={{ fontSize: "0.95rem", fontWeight: 700, color: T.red }}
              >
                {formatCurrency(row.amount)}
              </Typography>
            </Box>

            {row.remarks && (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.58rem",
                    color: T.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Remarks
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: T.text, fontWeight: 500 }}
                >
                  {row.remarks}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
export default function ExpenseReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  const [expandedExpenseId, setExpandedExpenseId] = useState(null);
  const [vehicleWiseSummary, setVehicleWiseSummary] = useState([]);
  const [exporting, setExporting] = useState(false);

  const handleVehicleToggle = (id) =>
    setExpandedVehicleId((p) => (p === id ? null : id));
  const handleExpenseToggle = (id) =>
    setExpandedExpenseId((p) => (p === id ? null : id));

  const [selectedVehicle, setSelectedVehicle] = useState("all");
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);

  const [summary, setSummary] = useState({
    totalExpenses: 0,
    dieselTotal: 0,
    serviceTotal: 0,
    otherTotal: 0,
    expenseCount: 0,
  });

  const hasActiveFilters = selectedVehicle !== "all";

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await vehicleList("", "", 1);
        setVehicles(res?.data?.results || res?.results || []);
      } catch (e) {
        console.error(e);
      }
    };
    fetch();
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    setExpenses([]);
    setVehicleWiseSummary([]);

    try {
      const res = await getExpenseReport({
        vehicle_number: selectedVehicle !== "all" ? selectedVehicle.trim() : "",
        from_date: fromDate,
        to_date: toDate,
      });

      const lineItems = res?.line_items || [];
      const backendSummary = res?.summary || {};
      const summaryList = res?.vehicle_wise_summary || [];

      setExpenses(lineItems);
      setTotalCount(res?.count || 0);
      setVehicleWiseSummary(summaryList);
      setExpandedVehicleId(null);
      setExpandedExpenseId(null);

      setSummary({
        totalExpenses: backendSummary.total_expenses || 0,
        dieselTotal: backendSummary.diesel_total || 0,
        serviceTotal: backendSummary.service_total || 0,
        otherTotal: backendSummary.other_total || 0,
        expenseCount: backendSummary.expense_count || 0,
      });
    } catch (e) {
      console.error("Failed to load analytical report:", e);
      setExpenses([]);
      setTotalCount(0);
      setVehicleWiseSummary([]);
    } finally {
      setLoading(false);
    }
  }, [selectedVehicle, fromDate, toDate]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleClear = () => {
    setSelectedVehicle("all");
    const today = new Date();
    setFromDate(
      new Date(today.getFullYear(), today.getMonth(), 1)
        .toISOString()
        .split("T")[0],
    );
    setToDate(today.toISOString().split("T")[0]);
    setExpandedVehicleId(null);
    setExpandedExpenseId(null);
    setExpenses([]);
    setTotalCount(0);
    setVehicleWiseSummary([]);
    setSummary({
      totalExpenses: 0,
      dieselTotal: 0,
      serviceTotal: 0,
      otherTotal: 0,
      expenseCount: 0,
    });
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await exportExpenseReportExcel({
        vehicle_number: selectedVehicle !== "all" ? selectedVehicle : "",
        from_date: fromDate,
        to_date: toDate,
      });
    } catch (e) {
      console.error(e);
      alert("Failed to export Excel. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async () => {
    const XLSX = await import("xlsx-js-style");

    const headers = [
      "Sr No",
      "Date",
      "Vehicle No.",
      "Expense Type",
      "Description",
      "Amount",
      "Vendor",
      "KM Reading",
      "Remarks",
    ];

    const rows = expenses.map((e, i) => [
      i + 1,
      e.expense_date || "—",
      e.vehicle_number || "—",
      EXPENSE_TYPE_CONFIG[e.expense_type]?.label || e.expense_type || "—",
      e.description || "—",
      e.amount ?? "—",
      e.vendor_name || "—",
      e.km_reading || "—",
      e.remarks || "—",
    ]);

    // ── Style Definitions ──────────────────────────────────────────
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "4F46E5" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "3730A3" } },
        bottom: { style: "thin", color: { rgb: "3730A3" } },
        left: { style: "thin", color: { rgb: "3730A3" } },
        right: { style: "thin", color: { rgb: "3730A3" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "F5F3FF" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: false },
      border: {
        top: { style: "thin", color: { rgb: "E0E7FF" } },
        bottom: { style: "thin", color: { rgb: "E0E7FF" } },
        left: { style: "thin", color: { rgb: "E0E7FF" } },
        right: { style: "thin", color: { rgb: "E0E7FF" } },
      },
    };

    const cellStyleOdd = {
      ...cellStyleEven,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    const numberStyle = {
      ...cellStyleEven,
      font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
    };

    const numberStyleOdd = {
      ...numberStyle,
      fill: { fgColor: { rgb: "FFFFFF" } },
    };

    // ── Build Worksheet Data ───────────────────────────────────────
    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val, colIdx) => {
          const isEven = rowIdx % 2 === 0;
          const isNumber = colIdx === 0;

          let style;
          if (isNumber) {
            style = isEven ? numberStyle : numberStyleOdd;
          } else {
            style = isEven ? cellStyleEven : cellStyleOdd;
          }

          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: style,
          };
        }),
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // ── Column Widths (no wrapping, width based on longest content) ──
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [
        headers[colIdx],
        ...rows.map((r) => String(r[colIdx] ?? "")),
      ];
      const maxLen = Math.max(...allValues.map((v) => v.length));
      return { wch: maxLen + 4 };
    });

    ws["!cols"] = colWidths;

    // ── Row Heights ────────────────────────────────────────────────
    ws["!rows"] = [{ hpt: 18 }];

    // ── Freeze Header Row ──────────────────────────────────────────
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: "Expense Report",
      Author: "Staff Management System",
      CreatedDate: new Date(),
    };

    XLSX.utils.book_append_sheet(wb, ws, "Expense Report");
    XLSX.writeFile(wb, `Expense_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -4, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stats Cards ── */}
      <Box sx={{ mt: 2, mb: 1.5, width: "100%" }}>
        <Grid
          container
          spacing={0.75}
          sx={{
            mx: 0,
            width: "100%",
            "& > .MuiGrid-item": { px: "3px !important", py: "3px !important" },
          }}
        >
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Total Expenses"
              value={formatCurrency(summary.totalExpenses)}
              color={T.primary}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Diesel"
              value={formatCurrency(summary.dieselTotal)}
              color="#d97706"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Service & Repair"
              value={formatCurrency(summary.serviceTotal)}
              color="#7c3aed"
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Other"
              value={formatCurrency(summary.otherTotal)}
              color={T.success}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Toolbar (single line) ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* Vehicle Select */}
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              select
              size="small"
              value={selectedVehicle}
              onChange={(e) => setSelectedVehicle(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CarIcon sx={{ fontSize: 14, color: T.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={selectFilterSx}
            >
              <MenuItem value="all" sx={{ fontSize: "0.72rem" }}>
                All Vehicles
              </MenuItem>
              {vehicles.map((v) => (
                <MenuItem
                  key={v.vehicle_id || v.vehicle_number}
                  value={v.vehicle_number}
                  sx={{ fontSize: "0.72rem" }}
                >
                  {v.vehicle_number}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* From Date */}
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon sx={{ fontSize: 13, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* To Date */}
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarIcon sx={{ fontSize: 13, color: T.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={filterInputSx}
            />
          </Grid>

          {/* Search — right after dates */}
          <Grid item xs="auto">
            <Tooltip title="Search" arrow>
              <Button
                size="small"
                onClick={fetchExpenses}
                disabled={loading}
                sx={{ ...primaryBtn, height: 34, minWidth: 34, px: 1 }}
              >
                {loading ? (
                  <CircularProgress size={14} sx={{ color: T.white }} />
                ) : (
                  <SearchIcon sx={{ fontSize: 15 }} />
                )}
              </Button>
            </Tooltip>
          </Grid>

          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />

          {/* Clear */}
          <Grid item xs="auto">
            <Tooltip title="Clear Filters" arrow>
              <span>
                <Button
                  size="small"
                  onClick={handleClear}
                  sx={{
                    ...dangerBtn,
                    height: 34,
                    minWidth: 34,
                    px: 1,
                    "&.Mui-disabled": {
                      borderColor: T.border,
                      bgcolor: T.white,
                      color: T.textMuted,
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Button>
              </span>
            </Tooltip>
          </Grid>

          {/* Export CSV */}
          {/* <Grid item xs="auto">
            <Tooltip title="Export CSV" arrow>
              <Button
                size="small"
                onClick={handleExportCSV}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <DownloadIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid> */}
          <Grid item xs="auto">
            <Tooltip title="Export Excel" arrow>
              <Box component="span" sx={{ display: "inline-block" }}>
                <Button
                  size="small"
                  onClick={handleExportExcel}
                  disabled={exporting || loading}
                  sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
                >
                  {exporting ? (
                    <CircularProgress size={14} sx={{ color: T.primary }} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 15 }} />
                  )}
                </Button>
              </Box>
            </Tooltip>
          </Grid>

          {/* Print */}
          <Grid item xs="auto">
            <Tooltip title="Print" arrow>
              <Button
                size="small"
                onClick={() => window.print()}
                sx={{ ...iconBtn, height: 34, minWidth: 34, px: 1 }}
              >
                <PrintIcon sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
      {/* ── Mobile View ── */}
      {isMobile ? (
        <Box>
          {loading && expenses.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 10,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={32} />
              <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
                Loading…
              </Typography>
            </Box>
          ) : expenses.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                border: `1.5px dashed ${T.border}`,
                bgcolor: T.primaryBg,
                ...sq,
              }}
            >
              <ReceiptIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
              <Typography
                sx={{ fontWeight: 700, color: T.text, fontSize: "0.9rem" }}
              >
                No expenses found
              </Typography>
            </Box>
          ) : (
            <Box
              id="expense-mobile-scroll"
              sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}
            >
              {/* Vehicle Summary */}
              {vehicleWiseSummary.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.6,
                      bgcolor: T.primaryBg,
                      border: `1.5px solid ${T.border}`,
                      ...sq,
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.7rem",
                        color: T.primary,
                      }}
                    >
                      🚛 Vehicle-wise Summary
                    </Typography>
                  </Box>
                  {vehicleWiseSummary.map((item, idx) => (
                    <MobileVehicleSummaryCard
                      key={item.vehicle}
                      item={item}
                      idx={idx}
                      expanded={expandedVehicleId === item.vehicle}
                      onToggle={() => handleVehicleToggle(item.vehicle)}
                    />
                  ))}
                </Box>
              )}
              {expenses.map((row, idx) => (
                <MobileExpenseCard
                  key={row.expense_id || idx}
                  row={row}
                  idx={idx}
                  expanded={expandedExpenseId === (row.expense_id || idx)}
                  onToggle={() => handleExpenseToggle(row.expense_id || idx)}
                />
              ))}

              {/* Summary Footer */}
              <Paper
                elevation={0}
                sx={{
                  ...sq,
                  border: `1.5px solid ${T.border}`,
                  bgcolor: T.primaryLight,
                  p: 1.5,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: T.primary,
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  📊 Grand Summary
                </Typography>
                <Grid container spacing={0.75}>
                  {[
                    [
                      "Total Expenses",
                      formatCurrency(summary.totalExpenses),
                      T.primary,
                    ],
                    ["Diesel", formatCurrency(summary.dieselTotal), "#d97706"],
                    [
                      "Service/Repair",
                      formatCurrency(summary.serviceTotal),
                      "#7c3aed",
                    ],
                    ["Other", formatCurrency(summary.otherTotal), T.success],
                  ].map(([l, v, c]) => (
                    <Grid item xs={6} key={l}>
                      <Typography
                        sx={{
                          fontSize: "0.58rem",
                          color: T.textMuted,
                          fontWeight: 600,
                          textTransform: "uppercase",
                        }}
                      >
                        {l}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.75rem", fontWeight: 700, color: c }}
                      >
                        {v}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop View ── */
        <>
          {/* Vehicle-wise Summary Table */}
          {vehicleWiseSummary.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                ...sq,
                border: `1.5px solid ${T.border}`,
                mb: 1.5,
                overflow: "hidden",
                boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 0.75,
                  bgcolor: T.primaryBg,
                  borderBottom: `1px solid ${T.border}`,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    color: T.primary,
                  }}
                >
                  📊 Vehicle-wise Summary
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 240 }}>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    "& td, & th": {
                      fontSize: "0.75rem",
                      py: 0.2,
                      px: 0.75,
                      borderBottom: `1px solid #ccc`,
                      textAlign: "center",
                      height: 26,
                    },
                    "& th": {
                      fontWeight: 700,
                      color: T.headerText,
                      bgcolor: `${T.headerBg} !important`,
                      fontSize: "0.8rem",
                      letterSpacing: "0.01em",
                      borderBottom: `1px solid #ccc`,
                      height: 28,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        "#",
                        "Vehicle No.",
                        "Total",
                        "Diesel",
                        "Service",
                        "Other",
                        "Count",
                      ].map((h) => (
                        <TableCell
                          key={h}
                          sx={{
                            bgcolor: `${T.headerBg} !important`,
                            color: `${T.headerText} !important`,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicleWiseSummary.map((item, i) => {
                      const isEven = i % 2 === 0;
                      const rowBg = isEven ? T.baseRow : T.altRow;

                      return (
                        <TableRow
                          key={item.vehicle}
                          sx={{
                            "& > td": { bgcolor: rowBg },
                            "&:hover > td": {
                              bgcolor: `${T.rowHover} !important`,
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                fontWeight: 700,
                                color: T.textMuted,
                              }}
                            >
                              {i + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: T.primary,
                                fontSize: "0.75rem",
                              }}
                            >
                              {item.vehicle}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: T.red,
                                fontSize: "0.75rem",
                              }}
                            >
                              {formatCurrency(item.total)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{ color: "#d97706", fontSize: "0.75rem" }}
                          >
                            {formatCurrency(item.diesel)}
                          </TableCell>
                          <TableCell
                            sx={{ color: "#7c3aed", fontSize: "0.75rem" }}
                          >
                            {formatCurrency(item.service)}
                          </TableCell>
                          <TableCell
                            sx={{ color: T.success, fontSize: "0.75rem" }}
                          >
                            {formatCurrency(item.other)}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.75rem" }}>
                            {item.count}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Expense Details Table */}
           <Paper
            elevation={0}
            sx={{
              ...sq,
              border: `1.5px solid ${T.border}`,
              overflow: "hidden",
              boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
            }}
          >
            <Box id="expense-desktop-scroll">
              <TableContainer 
                sx={{ 
                  maxHeight: "calc(100vh - 320px)", 
                  overflow: "auto" 
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 900,
                    borderCollapse: "separate",
                    "& td, & th": {
                      py: 0,
                      px: 0.75,
                      fontSize: "0.75rem",
                      borderBottom: `1px solid #ccc`,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                    },
                    "& th": {
                      fontWeight: 700,
                      color: T.headerText,
                      bgcolor: `${T.headerBg} !important`,
                      py: 0.2,
                      height: 28,
                      fontSize: "0.8rem",
                      letterSpacing: "0.01em",
                      borderBottom: `1px solid #ccc`,
                      lineHeight: 1.2,
                      zIndex: 10, 
                    },
                    "& td": {
                      height: 26,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      {[
                        { l: "#", sticky: true, left: 0, w: 35 },
                        { l: "Date", w: 90 },
                        { l: "Vehicle No.", sticky: true, left: 35, w: 100 },
                        { l: "Type", w: 100 },
                        { l: "Description", w: 180 },
                        { l: "Amount", w: 90 },
                        { l: "Vendor", w: 120 },
                        { l: "KM Reading", w: 90 },
                        { l: "Payment Mode", w: 100 },
                        { l: "Remarks", w: 130 },
                      ].map(({ l, sticky, left, w }) => (
                        <TableCell
                          key={l}
                          sx={{
                            minWidth: w,
                            bgcolor: `${T.headerBg} !important`,
                            color: `${T.headerText} !important`,
                            ...(sticky && {
                              position: "sticky",
                              left,
                              zIndex: 12,
                            }),
                          }}
                        >
                          {l}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading && expenses.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <CircularProgress
                            sx={{ color: T.primary }}
                            size={32}
                          />
                        </TableCell>
                      </TableRow>
                    ) : expenses.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          sx={{ textAlign: "center", py: 6, border: "none" }}
                        >
                          <ReceiptIcon
                            sx={{
                              fontSize: 40,
                              color: T.border,
                              mb: 1,
                              display: "block",
                              mx: "auto",
                            }}
                          />
                          <Typography
                            sx={{
                              color: T.textSub,
                              fontSize: "0.8rem",
                              fontWeight: 600,
                            }}
                          >
                            No expenses found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      expenses.map((expense, idx) => {
                        const typeConfig =
                          EXPENSE_TYPE_CONFIG[expense.expense_type] ||
                          EXPENSE_TYPE_CONFIG.other;
                        const isEven = idx % 2 === 0;
                        const rowBg = isEven ? T.baseRow : T.altRow;
                        const sc = {
                          bgcolor: `${rowBg} !important`,
                          zIndex: 5,
                        };

                        return (
                          <TableRow
                            key={expense.expense_id || idx}
                            sx={{
                              "& > td": {
                                bgcolor: rowBg,
                                transition: "background-color 0.12s",
                              },
                              "&:hover > td": {
                                bgcolor: `${T.rowHover} !important`,
                              },
                            }}
                          >
                            {/* # */}
                            <TableCell
                              sx={{ position: "sticky", left: 0, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 700,
                                  color: T.textMuted,
                                }}
                              >
                                {idx + 1}
                              </Typography>
                            </TableCell>

                            {/* Date */}
                            <TableCell
                              sx={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                            >
                              {formatDate(expense.expense_date)}
                            </TableCell>

                            {/* Vehicle No. */}
                            <TableCell
                              sx={{ position: "sticky", left: 35, ...sc }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: T.primary,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {expense.vehicle_number || "—"}
                              </Typography>
                            </TableCell>

                            {/* Type */}
                            <TableCell>
                              <Chip
                                icon={React.cloneElement(typeConfig.icon, {
                                  sx: { fontSize: 10 },
                                })}
                                label={typeConfig.label}
                                size="small"
                                sx={{
                                  ...sq,
                                  bgcolor: typeConfig.bg,
                                  color: typeConfig.color,
                                  fontSize: "0.6rem",
                                  height: 16,
                                  border: `1px solid ${typeConfig.color}30`,
                                  "& .MuiChip-icon": {
                                    color: typeConfig.color,
                                    ml: 0.5,
                                  },
                                  "& .MuiChip-label": { px: 0.75 },
                                }}
                              />
                            </TableCell>

                            {/* Description */}
                            <TableCell>
                              <Tooltip title={expense.description || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    maxWidth: 180,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {expense.description || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>

                            {/* Amount */}
                            <TableCell>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: T.red,
                                  fontSize: "0.75rem",
                                }}
                              >
                                {formatCurrency(expense.amount)}
                              </Typography>
                            </TableCell>

                            {/* Vendor */}
                            <TableCell sx={{ fontSize: "0.75rem" }}>
                              {expense.vendor_name || "—"}
                            </TableCell>

                            {/* KM Reading */}
                            <TableCell sx={{ fontSize: "0.75rem" }}>
                              {expense.km_reading
                                ? `${expense.km_reading} km`
                                : "—"}
                            </TableCell>

                            {/* Payment Mode */}
                            <TableCell sx={{ fontSize: "0.75rem" }}>
                              {expense.payment_mode || "—"}
                            </TableCell>

                            {/* Remarks */}
                            <TableCell>
                              <Tooltip title={expense.remarks || ""} arrow>
                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    maxWidth: 130,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {expense.remarks || "—"}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}

                    {/* Totals Row */}
                    {expenses.length > 0 && (
                      <TableRow
                        sx={{
                          position: "sticky",
                          bottom: 0,
                          zIndex: 20,
                          "& td": {
                            bgcolor: T.successBg,
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            py: 0.5,
                            borderTop: `2px solid ${T.primary}`,
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 22,
                            bgcolor: `${T.successBg} !important`,
                          }}
                        >
                          —
                        </TableCell>
                        <TableCell />
                        <TableCell
                          sx={{
                            position: "sticky",
                            left: 35,
                            zIndex: 22,
                            color: T.primary,
                            fontWeight: 800,
                            bgcolor: `${T.successBg} !important`,
                          }}
                        >
                          Totals
                        </TableCell>
                        <TableCell colSpan={2} />
                        <TableCell sx={{ color: T.red, fontWeight: 800 }}>
                          {formatCurrency(summary.totalExpenses)}
                        </TableCell>
                        <TableCell colSpan={4} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
}
